/**
 * ProfileEditor — patient-facing demographics review/edit step.
 *
 * Shown after the patient passes the DOB challenge and the bridge has
 * decrypted the preloaded profile, but BEFORE the sign-and-submit step
 * in BridgeFieldsForm. The patient sees the 13 demographic fields the
 * Mac sent across, can fix anything that's wrong, and toggles whether
 * the corrections should be written back to their file.
 *
 * Contract with the parent:
 *   - `initialProfile` (object) — snake_case dict that came out of
 *     `fetchEncryptedProfile()`. Only the 13 editable keys are read; the
 *     rest pass through untouched in the submission payload.
 *   - `onChange({ profile, updateMyFile, editedKeys })` — fires on every
 *     edit and on every toggle. `profile` is the *full* snake_case dict
 *     (initial + edits), `editedKeys` is the set of keys the patient
 *     actually touched (so callers can render an "edited" indicator
 *     and the bridge_sync can skip an unnecessary profile upsert).
 *   - `onContinue()` — fires when the patient clicks "Continue to sign".
 *
 * Inline styles only, to match the rest of patient-ui-app. No Tailwind.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PatientProfile, PatientInsurance } from "../../types/shared";
import {
  FORMATS,
  formatValue,
  maskedChange,
  isFormatValid,
  validationMessage,
  suggestDomainTypo,
} from "../../shared/lib/inputFormats";

// Fields the patient is allowed to edit (matches _PROFILE_PRELOAD_KEYS
// subset called "patient-editable" in routers/bridge.py).
const EDITABLE_KEYS = [
  "first_name",
  "last_name",
  "date_of_birth",
  "gender",
  "health_care_number",
  "email",
  "phone",
  "address",
  "city",
  "province",
  "postal_code",
  "insurance_provider",
  "plan_number",
  "insurance_id",
];

// Every wired-format field must be empty-or-valid to continue. health_card_ns
// is excluded (out-of-province cards warn but never block) per user decision.
const FORMAT_FIELDS: Record<string, string> = {
  date_of_birth: "date_iso",
  phone: "phone_na",
  postal_code: "postal_ca",
  email: "email",
};

const FIELD_LABELS: Record<string, string> = {
  date_of_birth: "Date of Birth",
  phone: "Phone",
  postal_code: "Postal Code",
  email: "Email",
};

const GENDER_OPTIONS = [
  { value: "Female", label: "Female" },
  { value: "Male", label: "Male" },
  { value: "Other", label: "Other" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

const PROVINCE_OPTIONS = [
  "NS", "NB", "PE", "NL", "QC", "ON",
  "MB", "SK", "AB", "BC", "YT", "NT", "NU",
];

// Profiles store province inconsistently — some as a 2-letter code ("NS"),
// some as a full name ("Nova Scotia"). The <select> options are codes only,
// so a full name matches nothing and renders "—". Normalize on read.
const PROVINCE_NAME_TO_CODE: Record<string, string> = {
  "newfoundland and labrador": "NL", "newfoundland": "NL", "labrador": "NL",
  "prince edward island": "PE",
  "nova scotia": "NS",
  "new brunswick": "NB",
  "quebec": "QC", "québec": "QC",
  "ontario": "ON",
  "manitoba": "MB",
  "saskatchewan": "SK",
  "alberta": "AB",
  "british columbia": "BC",
  "yukon": "YT",
  "northwest territories": "NT",
  "nunavut": "NU",
};
function _toProvinceCode(v: unknown): string {
  if (!v) return "";
  const s = String(v).trim();
  if (PROVINCE_OPTIONS.includes(s.toUpperCase())) return s.toUpperCase();
  return PROVINCE_NAME_TO_CODE[s.toLowerCase()] || s;
}

const COLORS = {
  background: "#f5f0eb",
  panel: "white",
  primary: "#182a33",
  muted: "#5b6e7c",
  subtle: "#5b6e7c",
  border: "#e6dccb",
  borderSoft: "rgba(42, 77, 105, 0.1)",
  edited: "#c9772a",      // warm amber — "edited but not saved yet"
  editedBg: "rgba(230, 140, 50, 0.08)",
  inputBg: "#f5f0eb",
};

interface ProfileEditorChange {
  profile: PatientProfile;
  updateMyFile: boolean;
  editedKeys: string[];
}

interface FocusFieldKey {
  key: string;
  nonce?: number;
}

interface ProfileEditorProps {
  initialProfile?: PatientProfile | null;
  onChange?: (change: ProfileEditorChange) => void;
  onContinue?: () => void;
  focusFieldKey?: FocusFieldKey | null;
}

interface RenderTextFieldOpts {
  format?: string;
  type?: string;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: string;
  helper?: string;
}

export default function ProfileEditor({
  initialProfile,
  onChange,
  onContinue,
  // ── CLICK-TO-JUMP (Agent B) ──────────────────────────────────────
  // When BridgeFieldsForm receives a click on the PDF, it sets
  // focusFieldKey to {key: "<editable_key>", nonce: <ts>}. The
  // useEffect below focuses + scrolls the matching input into view.
  // Re-clicking the same field requires a fresh nonce because React
  // bails on identical props otherwise — the parent always bumps it.
  focusFieldKey = null,
}: ProfileEditorProps) {
  // Per-field refs the click-to-jump effect uses to focus/scroll.
  const fieldRefs = useRef<Record<string, HTMLElement>>({});
  // Snapshot the original values so we can compute "edited" status as
  // the patient types. useMemo so we don't reset the baseline on every
  // re-render of the parent.
  const baseline = useMemo(() => {
    const b: Record<string, string> = {};
    for (const k of EDITABLE_KEYS) {
      b[k] = (initialProfile?.[k] as string | undefined) ?? "";
    }
    // Map a stored full province name to its 2-letter code so the <select>
    // shows it instead of falling back to "—".
    b.province = _toProvinceCode(b.province);
    // Insurance plan/member/provider canonically live in the `insurance`
    // sub-doc; fall back to it when the flat key is empty so prefilled values
    // show in the editor (and auto-fill reads them back from the sub-doc).
    const ins: PatientInsurance = initialProfile?.insurance || {};
    if (!b.plan_number) b.plan_number = ins.plan_number ?? "";
    if (!b.insurance_id) b.insurance_id = ins.member_id ?? "";
    if (!b.insurance_provider) b.insurance_provider = ins.provider ?? "";
    // Seed through the display formatter: inputs render formatValue(raw) while
    // the gate validates raw, so a stored "B3H1A1" looked right on screen but
    // held Continue disabled with nothing highlighted to fix. See the same
    // note in PatientConfirmDetails.
    for (const k of Object.keys(FORMAT_FIELDS)) {
      if (b[k]) b[k] = String(formatValue(FORMAT_FIELDS[k], b[k]) ?? "");
    }
    return b;
  }, [initialProfile]);

  const [values, setValues] = useState<Record<string, string>>(() => ({ ...baseline }));
  const [updateMyFile, setUpdateMyFile] = useState(true);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Recompute editedKeys whenever values change.
  const editedKeys = useMemo(() => {
    return EDITABLE_KEYS.filter(
      (k) => String(values[k] || "") !== String(baseline[k] || "")
    );
  }, [values, baseline]);

  // ── CLICK-TO-JUMP focus handler (Agent B) ───────────────────────────
  // When the parent passes a fresh focusFieldKey, scroll the matching
  // input into view and focus it. Re-keys via `nonce` so the same field
  // re-clicked twice still re-triggers focus. Safe to call against any
  // EDITABLE_KEYS entry — radios (gender) get scrolled but not focused
  // since the wrapper div isn't focusable.
  useEffect(() => {
    if (!focusFieldKey?.key) return;
    const el = fieldRefs.current[focusFieldKey.key];
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (_) {
      // Older browsers / jsdom — ignore.
    }
    // requestAnimationFrame lets the scroll start before focus steals
    // attention (and prevents a Safari-on-iOS keyboard pop while the
    // panel is still moving).
    requestAnimationFrame(() => {
      if (typeof el.focus === "function") {
        try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
      }
    });
  }, [focusFieldKey]);

  // Build the full profile dict the submission should carry — start from
  // the original (so non-editable keys like pharmacy_*, physician_*,
  // signature, consent pass through unchanged) and overlay edits.
  function buildFullProfile(nextValues: Record<string, string>): PatientProfile {
    const full: Record<string, unknown> = { ...(initialProfile || {}) };
    for (const k of EDITABLE_KEYS) {
      full[k] = nextValues[k] ?? "";
    }
    // Keep the structured `insurance` sub-doc in sync with the editable
    // insurance fields — auto-fill (and the form) read plan_number / member_id
    // / provider from the sub-doc, not the flat keys.
    full.insurance = {
      ...(initialProfile?.insurance || {}),
      provider: full.insurance_provider || "",
      member_id: full.insurance_id || "",
      plan_number: full.plan_number || "",
    };
    return full as PatientProfile;
  }

  // Fire onChange every time anything moves.
  function emit(nextValues: Record<string, string>, nextUpdateMyFile: boolean) {
    onChange?.({
      profile: buildFullProfile(nextValues),
      updateMyFile: nextUpdateMyFile,
      editedKeys: EDITABLE_KEYS.filter(
        (k) => String(nextValues[k] || "") !== String(baseline[k] || "")
      ),
    });
  }

  function setField(key: string, value: string) {
    setTouched((t) => ({ ...t, [key]: true }));
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      // emit asynchronously after state settles — but the parent only
      // needs the current snapshot, so emit synchronously with `next`.
      emit(next, updateMyFile);
      return next;
    });
  }

  function setUpdateFlag(checked: boolean) {
    setUpdateMyFile(checked);
    emit(values, checked);
  }

  // Basic email validation (only when the patient typed something).
  const emailValid =
    !values.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.email).trim());
  const emailTypoSuggestion = suggestDomainTypo(values.email);

  const badFormatKeys = Object.keys(FORMAT_FIELDS).filter(
    (k) => !isFormatValid(FORMAT_FIELDS[k], values[k])
  );
  const formatsOk = badFormatKeys.length === 0;

  const requiredOk =
    String(values.first_name || "").trim().length > 0 &&
    String(values.last_name || "").trim().length > 0 &&
    String(values.date_of_birth || "").trim().length > 0;

  const canContinue = requiredOk && emailValid && formatsOk;

  // Convenience renderers ------------------------------------------------

  const inputBaseStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxSizing: "border-box",
    color: COLORS.primary,
    backgroundColor: COLORS.inputBg,
  };

  function fieldStyle(key: string): React.CSSProperties {
    const edited = editedKeys.includes(key);
    return {
      ...inputBaseStyle,
      borderColor: edited ? COLORS.edited : COLORS.border,
      backgroundColor: edited ? COLORS.editedBg : COLORS.inputBg,
    };
  }

  function renderLabel(text: string, key: string) {
    const edited = editedKeys.includes(key);
    return (
      <label
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 600,
          color: COLORS.subtle,
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {text}
        {edited && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              color: COLORS.edited,
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: 0,
            }}
          >
            edited
          </span>
        )}
      </label>
    );
  }

  // `opts.format` (a FORMATS key) turns the field into an as-you-type masked
  // input: value shown through the display formatter, onChange via maskedChange,
  // inputMode/maxLength/placeholder/autoComplete from the registry, date→text.
  // The ref registration is preserved so click-to-jump still focuses the field.
  // A non-empty invalid value shows an inline message; health_card_ns warns
  // only (out-of-province cards) and never blocks.
  function renderTextField(key: string, label: string, opts: RenderTextFieldOpts = {}) {
    const fmt = opts.format && FORMATS[opts.format] ? opts.format : null;
    const rawVal = values[key] || "";
    const displayVal = fmt ? formatValue(fmt, rawVal) : rawVal;
    // Also surface a PREFILLED invalid value — see PatientConfirmDetails.
    const showError =
      fmt && !isFormatValid(fmt, rawVal) && (touched[key] || rawVal === baseline[key]);
    const isHcn = fmt === "health_card_ns";
    return (
      <div style={{ marginBottom: 14 }}>
        {renderLabel(label, key)}
        {fmt ? (
          <input
            ref={(el) => { if (el) fieldRefs.current[key] = el; }}
            type={(opts.type || "text") === "date" ? "text" : (opts.type || "text")}
            value={displayVal as string}
            onChange={(e) => maskedChange(fmt, displayVal, e, (v) => setField(key, v))}
            placeholder={opts.placeholder || FORMATS[fmt].placeholder}
            inputMode={FORMATS[fmt].inputMode}
            maxLength={FORMATS[fmt].maxLength}
            autoComplete={opts.type === "date" ? "bday" : (opts.autoComplete || FORMATS[fmt].autoComplete)}
            style={fieldStyle(key)}
          />
        ) : (
          <input
            ref={(el) => { if (el) fieldRefs.current[key] = el; }}
            type={opts.type || "text"}
            value={values[key] || ""}
            onChange={(e) => setField(key, e.target.value)}
            placeholder={opts.placeholder || ""}
            inputMode={opts.inputMode}
            autoComplete={opts.autoComplete}
            style={fieldStyle(key)}
          />
        )}
        {showError && (
          <p style={{ color: isHcn ? COLORS.muted : "#c25450", fontSize: 12, margin: "4px 2px 0" }}>
            {validationMessage(fmt, rawVal)}
          </p>
        )}
        {opts.helper && (
          <p style={{ color: COLORS.muted, fontSize: 12, margin: "4px 2px 0" }}>
            {opts.helper}
          </p>
        )}
      </div>
    );
  }

  // Render ---------------------------------------------------------------

  return (
    <div className="hf-profile-editor-root">
      {/* ─────────────────────────────────────────────────────────────
          RESPONSIVE STYLES (Agent C — mobile/tablet)
          Container-level only — field logic and per-row layout are
          untouched. iOS zoom is suppressed by promoting input font to
          16px on mobile. Tap targets stay ≥44px via min-height.
          ───────────────────────────────────────────────────────────── */}
      <style>{`
        .hf-profile-editor-root {
          background-color: ${COLORS.background};
          padding: 32px 20px;
          min-height: 100%;
          box-sizing: border-box;
        }
        .hf-profile-editor-card {
          max-width: 640px;
          margin: 0 auto;
          background-color: ${COLORS.panel};
          border-radius: 14px;
          padding: 32px;
          box-shadow: 0 6px 24px rgba(0,0,0,0.06);
          box-sizing: border-box;
        }
        @media (max-width: 1024px) and (min-width: 641px) {
          .hf-profile-editor-root { padding: 20px 14px; }
          .hf-profile-editor-card { padding: 24px; }
        }
        @media (max-width: 640px) {
          .hf-profile-editor-root { padding: 16px 12px; }
          .hf-profile-editor-card {
            padding: 18px;
            border-radius: 12px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          }
          .hf-profile-editor-card input[type="text"],
          .hf-profile-editor-card input[type="email"],
          .hf-profile-editor-card input[type="tel"],
          .hf-profile-editor-card input[type="date"],
          .hf-profile-editor-card select {
            font-size: 16px !important;
            min-height: 44px;
          }
          .hf-profile-editor-card label[role],
          .hf-profile-editor-card button {
            min-height: 44px;
          }
          /* Continue button full-width on mobile. */
          .hf-profile-editor-card .hf-profile-continue-wrap {
            justify-content: stretch !important;
          }
          .hf-profile-editor-card .hf-profile-continue-wrap button {
            width: 100%;
            font-size: 16px !important;
          }
        }
      `}</style>
      <div className="hf-profile-editor-card">
        <h2 style={{ marginTop: 0, marginBottom: 6, color: COLORS.primary }}>
          Review your information
        </h2>
        <p
          style={{
            marginTop: 0,
            marginBottom: 24,
            color: COLORS.subtle,
            lineHeight: 1.5,
          }}
        >
          Here's what we have on file. Fix anything that's wrong or out of
          date — your physician will get the corrections.
        </p>

        {/* Your name */}
        <Section title="Your name">
          <Row>
            <Col>{renderTextField("first_name", "First name", { autoComplete: "given-name" })}</Col>
            <Col>{renderTextField("last_name", "Last name", { autoComplete: "family-name" })}</Col>
          </Row>
        </Section>

        {/* DOB */}
        <Section title="Date of birth">
          {renderTextField("date_of_birth", "Date of birth", {
            type: "date",
            autoComplete: "bday",
            helper: "YYYY-MM-DD",
            format: "date_iso",
          })}
        </Section>

        {/* Sex */}
        <Section title="Sex">
          {renderLabel("Sex", "gender")}
          <div
            ref={(el) => { if (el) fieldRefs.current.gender = el; }}
            style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
          >
            {GENDER_OPTIONS.map((opt) => {
              const selected = (values.gender || "") === opt.value;
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                    borderRadius: 999,
                    backgroundColor: selected ? COLORS.primary : COLORS.inputBg,
                    color: selected ? "white" : COLORS.primary,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={opt.value}
                    checked={selected}
                    onChange={(e) => setField("gender", e.target.value)}
                    style={{ display: "none" }}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </Section>

        {/* Health card */}
        <Section title="Health card">
          {renderTextField("health_care_number", "Health card number", {
            placeholder: "e.g. 1234 567 890",
            format: "health_card_ns",
          })}
        </Section>

        {/* Contact */}
        <Section title="Contact">
          <Row>
            <Col>{renderTextField("email", "Email", { type: "email", autoComplete: "email", placeholder: "you@example.com", format: "email" })}</Col>
            <Col>{renderTextField("phone", "Phone", { type: "tel", autoComplete: "tel", placeholder: "(902) 555-1234", format: "phone_na" })}</Col>
          </Row>
          {!emailValid && touched.email && (
            <p style={{ color: "#c25450", fontSize: 13, margin: "0 0 8px" }}>
              That doesn't look like a valid email address.
            </p>
          )}
          {emailTypoSuggestion && (
            <button
              type="button"
              onClick={() => setField("email", emailTypoSuggestion)}
              style={{
                background: "none", border: "none", padding: "0 0 8px", cursor: "pointer",
                color: COLORS.edited, fontSize: 13, textDecoration: "underline", fontFamily: "inherit",
              }}
            >
              Did you mean {emailTypoSuggestion}?
            </button>
          )}
        </Section>

        {/* Address */}
        <Section title="Address">
          {renderTextField("address", "Street address", {
            autoComplete: "street-address",
            placeholder: "123 Main St",
          })}
          <Row>
            <Col flex={2}>{renderTextField("city", "City", { autoComplete: "address-level2" })}</Col>
            <Col>
              {renderLabel("Province", "province")}
              <select
                ref={(el) => { if (el) fieldRefs.current.province = el; }}
                value={values.province || ""}
                onChange={(e) => setField("province", e.target.value)}
                style={fieldStyle("province")}
              >
                <option value="">—</option>
                {PROVINCE_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Col>
            <Col>
              {renderTextField("postal_code", "Postal code", {
                autoComplete: "postal-code",
                placeholder: "B3J 1A1",
                format: "postal_ca",
              })}
            </Col>
          </Row>
        </Section>

        {/* Insurance */}
        <Section title="Insurance">
          <Row>
            <Col>{renderTextField("insurance_provider", "Insurance provider", { placeholder: "e.g. Blue Cross" })}</Col>
            <Col>{renderTextField("plan_number", "Plan / contract number", { placeholder: "Plan or contract number" })}</Col>
          </Row>
          <Row>
            <Col>{renderTextField("insurance_id", "Member ID", { placeholder: "Insurance member ID" })}</Col>
            <Col>{/* spacer */}</Col>
          </Row>
        </Section>

        {/* Update-my-file flag */}
        <div
          style={{
            marginTop: 24,
            padding: "14px 16px",
            border: `1px solid ${COLORS.borderSoft}`,
            borderRadius: 10,
            backgroundColor: "#f5f0eb",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              cursor: "pointer",
              color: COLORS.primary,
              fontSize: 14,
              lineHeight: 1.5,
            }}
            title="Your physician will see the corrections next time."
          >
            <input
              type="checkbox"
              checked={updateMyFile}
              onChange={(e) => setUpdateFlag(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>Save these updates to my file.</strong>
              <br />
              <span style={{ color: COLORS.subtle, fontSize: 13 }}>
                Your physician will see the corrections next time. Uncheck to
                use these values for this form only.
              </span>
            </span>
          </label>
        </div>

        {/* Continue button */}
        <div
          className="hf-profile-continue-wrap"
          style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}
        >
          <button
            type="button"
            onClick={onContinue}
            disabled={!canContinue}
            style={{
              padding: "12px 24px",
              fontSize: 15,
              fontWeight: 600,
              border: "none",
              borderRadius: 999,
              backgroundColor: canContinue ? COLORS.primary : "#bbb",
              color: "white",
              cursor: canContinue ? "pointer" : "not-allowed",
            }}
          >
            Continue to sign →
          </button>
        </div>
        {!canContinue && (
          <p style={{ color: COLORS.muted, fontSize: 12, marginTop: 10, textAlign: "right" }}>
            {!formatsOk
              ? `Please check ${badFormatKeys.map((k) => FIELD_LABELS[k] || k).join(", ")}.`
              : "First name, last name, and date of birth are required."}
          </p>
        )}
      </div>
    </div>
  );
}


// Tiny layout helpers — keep them in the same file since they're only
// used here and the patient-ui-app convention is single-file components.

function Section({ title, children }: { title: React.ReactNode; children?: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <h3
        style={{
          fontSize: 13,
          color: "#5b6e7c",
          margin: "0 0 10px",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontWeight: 700,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Row({ children }: { children?: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      {children}
    </div>
  );
}

function Col({ children, flex = 1 }: { children?: React.ReactNode; flex?: number }) {
  return (
    <div style={{ flex: `${flex} 1 180px`, minWidth: 0 }}>
      {children}
    </div>
  );
}
